import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/admin/audit-logs?${params}`).then(({ data }) => {
      setLogs(data.data);
      setTotal(data.total);
    });
  }, [page, dateFrom, dateTo]);

  const exportUrl = (fmt) => {
    const params = new URLSearchParams({ fmt });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return `${API}/admin/audit-logs?${params}`;
  };

  const onFromChange = (e) => { setDateFrom(e.target.value); setPage(1); };
  const onToChange = (e) => { setDateTo(e.target.value); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Shell role="admin">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Audit Log</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Activity History</h1>
      </div>

      {/* Filters + Export */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">From</Label>
              <Input type="date" value={dateFrom} onChange={onFromChange} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">To</Label>
              <Input type="date" value={dateTo} onChange={onToChange} className="h-10" />
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} asChild size="sm" variant="outline" className="gap-1.5">
                <a href={exportUrl(f)} download>
                  <Download className="w-3.5 h-3.5" />{f.toUpperCase()}
                </a>
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Language</th>
              </tr>
            </thead>
            <tbody data-testid="audit-table-body">
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{l.action}</Badge></td>
                  <td className="px-4 py-3">{l.user_name}<div className="text-xs text-slate-500">{l.user_emp_code}</div></td>
                  <td className="px-4 py-3">{l.doctor_name || "—"}</td>
                  <td className="px-4 py-3 uppercase text-slate-600">{l.language_code || "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">No activity</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            {total} {total === 1 ? "entry" : "entries"}
            {totalPages > 1 && <> · page {page} of {totalPages}</>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </Shell>
  );
}
